import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  CalendarMonth as BookingsIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Pets as PetsIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import api from '../config/api';
import { PageLoader } from '../components/TableShared';

const StatCard = ({ title, value, icon, color }) => (
  <Paper sx={{ p: 2.5, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Typography color="textSecondary" variant="body2" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700 }}>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: color,
          borderRadius: '50%',
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
    </Box>
  </Paper>
);

const BOOKING_COLORS = ['#32A6D8', '#3FA477', '#E5A33D', '#FF6B6B', '#AA96DA'];

export default function Analytics() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  };

  const bookingsByStatusData = stats.bookingsByStatus
    ? Object.entries(stats.bookingsByStatus)
        .filter(([, val]) => val > 0)
        .map(([key, val]) => ({
          name: key.charAt(0).toUpperCase() + key.slice(1),
          value: val,
        }))
    : [];

  const sittersByStatusData = stats.sittersByStatus
    ? Object.entries(stats.sittersByStatus)
        .filter(([, val]) => val > 0)
        .map(([key, val]) => ({
          name: key.charAt(0).toUpperCase() + key.slice(1),
          value: val,
        }))
    : [];

  const overviewData = [
    { name: 'Users', value: stats.totalUsers || 0 },
    { name: 'Bookings', value: stats.totalBookings || 0 },
    { name: 'Pets', value: stats.totalPets || 0 },
    { name: 'Reviews', value: stats.totalReviews || 0 },
    { name: 'Sitters', value: stats.totalSitters || 0 },
    { name: 'Services', value: stats.totalServices || 0 },
  ];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <PageLoader />
      ) : (
        <>
          {/* Summary Cards Row 1 */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Users"
                value={(stats.totalUsers || 0).toLocaleString()}
                icon={<PeopleIcon sx={{ color: 'white' }} />}
                color="#32A6D8"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Bookings"
                value={(stats.totalBookings || 0).toLocaleString()}
                icon={<BookingsIcon sx={{ color: 'white' }} />}
                color="#005A64"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Revenue"
                value={`$${(stats.totalRevenue || 0).toFixed(2)}`}
                icon={<MoneyIcon sx={{ color: 'white' }} />}
                color="#3FA477"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Reviews"
                value={(stats.totalReviews || 0).toLocaleString()}
                icon={<StarIcon sx={{ color: 'white' }} />}
                color="#E5A33D"
              />
            </Grid>
          </Grid>

          {/* Summary Cards Row 2 */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active Bookings"
                value={(stats.activeBookings || 0).toLocaleString()}
                icon={<TrendingUpIcon sx={{ color: 'white' }} />}
                color="#AA96DA"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="New Users This Month"
                value={(stats.newUsersThisMonth || 0).toLocaleString()}
                icon={<PeopleIcon sx={{ color: 'white' }} />}
                color="#FFC2EB"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="New Bookings This Month"
                value={(stats.newBookingsThisMonth || 0).toLocaleString()}
                icon={<BookingsIcon sx={{ color: 'white' }} />}
                color="#FF6B6B"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Pets"
                value={(stats.totalPets || 0).toLocaleString()}
                icon={<PetsIcon sx={{ color: 'white' }} />}
                color="#4ECDC4"
              />
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Bookings by Status Pie Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                  Bookings by Status
                </Typography>
                {bookingsByStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={bookingsByStatusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={50}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {bookingsByStatusData.map((entry, index) => (
                          <Cell key={entry.name} fill={BOOKING_COLORS[index % BOOKING_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="textSecondary" sx={{ py: 8, textAlign: 'center' }}>
                    No booking data available yet.
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Sitters by Status Pie Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                  Sitters by Status
                </Typography>
                {sittersByStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={sittersByStatusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={50}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {sittersByStatusData.map((entry, index) => (
                          <Cell key={entry.name} fill={['#3FA477', '#32A6D8', '#FF6B6B'][index % 3]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="textSecondary" sx={{ py: 8, textAlign: 'center' }}>
                    No sitter data available yet.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Platform Overview Bar Chart */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
              Platform Overview
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={overviewData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" name="Count" fill="#32A6D8" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* Quick Stats Summary */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
              Platform Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#32A6D8' }}>
                    {stats.totalOwners || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Pet Owners</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#005A64' }}>
                    {stats.totalSitters || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Total Sitters</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#3FA477' }}>
                    {stats.sittersByStatus?.approved || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Approved Sitters</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#E5A33D' }}>
                    {stats.sittersByStatus?.pending || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Pending Approval</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
}
